import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import CircularProgress from '@mui/material/CircularProgress'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { toast } from 'react-toastify'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import ImageViewer from 'components/ImageViewer'
import PortfolioFolderTree from './PortfolioFolderTree'
import ManagePortfolioFolderPermissions from './ManagePortfolioFolderPermissions'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import PortfolioFilePreviewModal from './PortfolioFilePreviewModal'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import SharePortfolioFileModal from './SharePortfolioFileModal'
import PortfolioFileBuildingSharesModal from './PortfolioFileBuildingSharesModal'
import MoveDocumentModal from './MoveDocumentModal'
import BulkMoveDocumentsModal from './BulkMoveDocumentsModal'
import BulkShareToBpsModal from './BulkShareToBpsModal'
import EditDocumentModal from './EditDocumentModal'
import ConfirmModal from './ConfirmModal'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import useMarqueeSelection from 'hooks/useMarqueeSelection'

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']
const extOf = (f) => (f.displayName || f.name || '').split('.').pop().toLowerCase()
import {
  getPortfolioFolderTreeRoutine,
  getPortfolioFolderFilesRoutine,
  getPortfolioFolderUserCountsRoutine,
  unassignPortfolioFileRoutine,
  bulkRemovePortfolioFilesRoutine,
} from 'ducks/portfolioDocuments/actions'
import { selectPortfolioFolderFiles } from 'ducks/portfolioDocuments/selectors'
import { getFileTypesRoutine } from 'ducks/files/actions'
import ProcessingFilesPill from 'features/bpDetails/Components/files/ProcessingFilesPill'
import { getFileTypes } from 'ducks/files/selectors'
import {
  startPortfolioMultiUpload,
  presignPortfolioMultiUploadFile,
  addFileToPortfolioMultiUpload,
  getPortfolioMultiUploadProgress,
} from 'services/portfolioDocumentsService'
import { uploadFileToS3 } from 'services/fileService'

const rise = keyframes`from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none }`

const Layout = styled.div`
  display: flex;
  min-height: 460px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme?.colors?.mediumGrey || '#e6e6e6'};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(12, 18, 41, 0.04);

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: 0;
  }
`
const Main = styled.div`flex: 1; min-width: 0; padding: 20px 24px; display: flex; flex-direction: column; position: relative;`
const HeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'};
`
const Crumb = styled.div`display: flex; align-items: center; gap: 9px; min-width: 0;`
const FolderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Count = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
  background: ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'};
  padding: 2px 9px;
  border-radius: 999px;
  flex-shrink: 0;
`
const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`
const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 1;
  min-width: 220px;
  max-width: 360px;
  background: ${({ theme }) => theme?.colors?.lighterGrey || '#f7f7f9'};
  border: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#eceef1'};
  border-radius: 9px;
  padding: 7px 11px;
`
const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
`
const ClearBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: #9aa0ac;
  padding: 0 2px;
  &:hover { color: ${({ theme }) => theme?.colors?.primary || '#D24724'}; }
`
const ResultCount = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
`
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 9px 14px;
  border-radius: 10px;
  background: ${({ theme }) => theme?.colors?.linen || '#fbede9'};
  border: 1px solid ${({ theme }) => theme?.colors?.primary || '#D24724'}33;
`
const BulkCount = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.primary || '#D24724'};
`
const BulkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ $danger, theme }) => ($danger ? '#e0b4ab' : (theme?.colors?.lightGrey || '#e6e6e6'))};
  background: #fff;
  color: ${({ $danger, theme }) => ($danger ? '#b3331a' : (theme?.colors?.haiti || '#0C1229'))};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme?.colors?.lighterGrey || '#f7f7f9'}; }
`
const BulkSpacer = styled.span`flex: 1;`
const TextLink = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#777'};
  text-decoration: underline;
  &:hover { color: ${({ theme }) => theme?.colors?.primary || '#D24724'}; }
`
const UploadBtn = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: ${({ theme }) => theme?.colors?.primary || '#D24724'};
  color: #fff;
  border-radius: 9px;
  padding: 9px 16px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(210, 71, 36, 0.25);
  transition: filter 0.15s, transform 0.1s;
  opacity: ${({ $busy }) => ($busy ? 0.7 : 1)};
  pointer-events: ${({ $busy }) => ($busy ? 'none' : 'auto')};
  &:hover { filter: brightness(1.05); transform: translateY(-1px); }
  input { display: none; }
`
const FileList = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(212px, 1fr));
  gap: 14px;
  align-content: start;
  padding-bottom: 8px;
  user-select: none;
`
const SelectionOverlay = styled.div`
  position: absolute;
  border: 1px solid ${({ theme }) => theme?.colors?.secondary || '#33becc'};
  background: ${({ theme }) => theme?.colors?.secondary || '#33becc'}1f;
  border-radius: 2px;
  pointer-events: none;
  z-index: 5;
`
const FileCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ $selected, theme }) => ($selected ? (theme?.colors?.primary || '#D24724') : (theme?.colors?.lightGrey || '#f2f2f2'))};
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  animation: ${rise} 0.2s ease both;
  box-shadow: ${({ $selected }) => ($selected ? '0 0 0 1px #D24724 inset' : 'none')};
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  &:hover {
    border-color: ${({ $selected, theme }) => ($selected ? (theme?.colors?.primary || '#D24724') : (theme?.colors?.mediumGrey || '#e6e6e6'))};
    box-shadow: 0 6px 18px rgba(12, 18, 41, 0.08);
    transform: translateY(-2px);
  }
  &:hover .select-toggle { opacity: 1; }
`
const SelectToggle = styled.button`
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 2;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
  box-shadow: 0 1px 4px rgba(12, 18, 41, 0.18);
  opacity: ${({ $selected }) => ($selected ? 1 : 0)};
  transition: opacity 0.12s;
`
const Thumb = styled.button`
  position: relative;
  height: 120px;
  border: none;
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;
  background: ${({ $color }) => `${$color}14`};
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'};
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; -webkit-user-drag: none; user-select: none; }
`
const TypeTag = styled.span`
  position: absolute;
  left: 8px;
  bottom: 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
  background: ${({ $color }) => $color};
  padding: 3px 8px;
  border-radius: 6px;
  max-width: calc(100% - 16px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const CardBody = styled.div`padding: 10px 12px 6px; flex: 1;`
const CardName = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const CardSub = styled.div`
  font-size: 11.5px;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const CardDesc = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme?.colors?.darkGrey || '#555'};
  margin-top: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`
const CardMetaRow = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 6px;`
const MetaText = styled.span`font-size: 11.5px; color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};`
const BpChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${({ theme }) => `${theme?.colors?.secondary || '#33becc'}1f`};
  color: #1f8a96;
  border: none;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  &:hover { filter: brightness(0.96); }
`
const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-top: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'};
`
const IconAct = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ $danger, theme }) => ($danger ? theme?.colors?.error || '#d14925' : theme?.colors?.grey?.[800] || '#888')};
  padding: 6px;
  border-radius: 7px;
  text-decoration: none;
  &:hover { background: ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'}; color: ${({ $danger, theme }) => ($danger ? theme?.colors?.error || '#d14925' : theme?.colors?.primary || '#D24724')}; }
`
const LoadMore = styled.button`
  align-self: center;
  margin-top: 6px;
  background: transparent;
  color: ${({ theme }) => theme?.colors?.primary || '#D24724'};
  border: 1px solid ${({ theme }) => theme?.colors?.linen || '#fbede9'};
  border-radius: 9px;
  padding: 9px 18px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  &:hover { background: ${({ theme }) => theme?.colors?.linen || '#fbede9'}; }
`
const Placeholder = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${({ theme }) => theme?.colors?.grey?.[700] || '#b3b3b3'};
  text-align: center;
  padding: 40px;
  h4 { margin: 0; font-size: 15px; font-weight: 600; color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'}; }
  p { margin: 0; font-size: 13.5px; max-width: 320px; line-height: 1.5; }
`

const fileUrl = (f) => f.url || f.latestVersion?.url || (f.versions && f.versions[0] && f.versions[0].url) || null
const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const CATEGORY_COLOURS = ['#33becc', '#ffaf7f', '#f0b429', '#7c9eb2', '#b388eb', '#5fb49c', '#e08e8e', '#6c8ae4']
const catColour = (f) => {
  const key = String(f.fileType?.fileCategoryId || f.fileType?.id || f.fileType?.name || 'doc')
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return CATEGORY_COLOURS[h % CATEGORY_COLOURS.length]
}
const fileTypeName = (f) => f.fileType?.name || f.userFileType?.name || 'Document'
const uploaderName = (f) => {
  const u = f.uploader
  if (!u) return null
  return [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || null
}
const formatDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const STATUS_META = {
  completed: { label: 'Ready', c: '#1f8a96' },
  failed: { label: 'Failed', c: '#d14925' },
  new: { label: 'Queued', c: '#b58a00' },
}
const statusMeta = (s) => STATUS_META[s] || { label: 'Processing…', c: '#b58a00' }

const UploadPanel = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 330px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme?.colors?.mediumGrey || '#e6e6e6'};
  border-radius: 12px;
  box-shadow: 0 14px 40px rgba(12, 18, 41, 0.18);
  z-index: 1200;
  overflow: hidden;
`
const UploadPanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-size: 13.5px;
  font-weight: 600;
  color: #fff;
  background: ${({ theme }) => theme?.colors?.primary || '#D24724'};
`
const Bar = styled.div`height: 4px; background: #f2f2f2;`
const BarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ theme }) => theme?.colors?.secondary || '#33becc'};
  transition: width 0.3s ease;
`
const UploadListBox = styled.div`max-height: 210px; overflow-y: auto; padding: 6px 6px 10px;`
const UploadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  font-size: 12.5px;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`
const StatusPill = styled.span`
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  color: ${({ $c }) => $c};
  background: ${({ $c }) => `${$c}1f`};
`

export default function PortfolioDocumentsTab({ portfolioId, canEdit, buildingPassports = [] }) {
  const dispatch = useDispatch()
  const uploadInputRef = useRef(null)
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [accessFolder, setAccessFolder] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [insightFile, setInsightFile] = useState(null)
  const [shareFile, setShareFile] = useState(null)
  const [bpSharesFile, setBpSharesFile] = useState(null)
  const [moveFileItem, setMoveFileItem] = useState(null)
  const [editFile, setEditFile] = useState(null)
  const [removeFileItem, setRemoveFileItem] = useState(null)
  const [search, setSearch] = useState('')
  const [fileTypeIds, setFileTypeIds] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false)
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false)
  const [bulkShareOpen, setBulkShareOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null)
  const pollRef = useRef(null)
  const fileListRef = useRef(null)

  useEffect(() => () => clearTimeout(pollRef.current), [])
  const folderId = selectedFolder?.id
  const { items, total, page } = useSelector(selectPortfolioFolderFiles(folderId || '__none__'))
  const fileTypesRaw = useSelector(getFileTypes)
  const fileTypeOptions = Array.isArray(fileTypesRaw)
    ? [...fileTypesRaw].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : []
  const fileTypeKey = fileTypeIds.join(',')
  const selectedTypeOptions = fileTypeOptions.filter((t) => fileTypeIds.includes(t.id))

  useEffect(() => {
    dispatch(getPortfolioFolderTreeRoutine.trigger(portfolioId))
    dispatch(getFileTypesRoutine.trigger())
    if (canEdit) dispatch(getPortfolioFolderUserCountsRoutine.trigger(portfolioId))
  }, [dispatch, portfolioId, canEdit])

  useEffect(() => { setSearch(''); setFileTypeIds([]) }, [folderId])
  useEffect(() => { setSelectedIds([]) }, [folderId, search, fileTypeKey])

  const toggleSelect = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))
  const clearSelection = () => setSelectedIds([])
  const allVisibleSelected = items.length > 0 && selectedIds.length === items.length
  const toggleSelectAll = () =>
    setSelectedIds(allVisibleSelected ? [] : items.map((f) => f.id))
  const bulkRemove = () =>
    dispatch(bulkRemovePortfolioFilesRoutine.trigger({ portfolioId, fileIds: selectedIds, folderId }))

  const { isDragSelecting, selectionRect, handleMouseDown, handleContentClick } = useMarqueeSelection({
    containerRef: fileListRef,
    selectedIds,
    onChange: setSelectedIds,
    disabled: !canEdit
  })

  useEffect(() => {
    if (!folderId) return undefined
    const t = setTimeout(() => {
      dispatch(getPortfolioFolderFilesRoutine.trigger({ portfolioId, folderId, page: 1, search, fileTypeIds }))
    }, search ? 300 : 0)
    return () => clearTimeout(t)
  }, [dispatch, portfolioId, folderId, search, fileTypeKey])

  const loadMore = () =>
    dispatch(getPortfolioFolderFilesRoutine.trigger({ portfolioId, folderId, page: (page || 1) + 1, search, fileTypeIds }))

  const remove = (fileId) =>
    dispatch(unassignPortfolioFileRoutine.trigger({ portfolioId, fileId, folderId }))

  const refreshFiles = () => {
    if (folderId) dispatch(getPortfolioFolderFilesRoutine.trigger({ portfolioId, folderId, page: 1, search, fileTypeIds }))
    dispatch(getPortfolioFolderTreeRoutine.trigger(portfolioId))
  }

  const pollProgress = (processId, attempt = 0) => {
    getPortfolioMultiUploadProgress(portfolioId, processId)
      .then((res) => {
        const prog = res.data.data
        setUploadProgress({ phase: 'processing', ...prog })
        if (prog.completed > 0) refreshFiles()
        if (prog.done || attempt > 90) {
          refreshFiles()
          pollRef.current = setTimeout(() => setUploadProgress(null), 3500)
        } else {
          pollRef.current = setTimeout(() => pollProgress(processId, attempt + 1), 2000)
        }
      })
      .catch(() => {
        if (attempt > 90) { setUploadProgress(null); return }
        pollRef.current = setTimeout(() => pollProgress(processId, attempt + 1), 3000)
      })
  }

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return

    clearTimeout(pollRef.current)
    setBusy(true)
    setUploadProgress({ phase: 'uploading', total: files.length, uploaded: 0, files: [], completed: 0, failed: 0 })
    try {
      const { data: startData } = await startPortfolioMultiUpload(portfolioId, files.length)
      const processId = startData.data.id

      let uploaded = 0
      for (const file of files) {
        const { data } = await presignPortfolioMultiUploadFile(portfolioId, processId, file.name)
        const { presignedUrl, s3Key } = data.data
        await uploadFileToS3(presignedUrl, file)
        await addFileToPortfolioMultiUpload(portfolioId, processId, {
          s3Key,
          displayName: file.name,
          originalFileName: file.name,
          size: file.size,
          mimeType: file.type,
          portfolioFolderId: folderId,
        })
        uploaded += 1
        setUploadProgress({ phase: 'uploading', total: files.length, uploaded, files: [], completed: 0, failed: 0 })
      }

      setBusy(false)
      pollProgress(processId)
    } catch (err) {
      setUploadProgress(null)
      toast.error('Upload failed. Please try again.')
      setBusy(false)
    }
  }

  const handleUpload = (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    uploadFiles(files)
  }

  return (
    <>
      <Layout>
        <PortfolioFolderTree
          portfolioId={portfolioId}
          canEdit={canEdit}
          selectedFolder={selectedFolder}
          onSelect={setSelectedFolder}
          onManageAccess={setAccessFolder}
        />
        <Main>
          <ProcessingFilesPill type='portfolio' id={portfolioId} onCleared={refreshFiles} onRetry={canEdit ? () => uploadInputRef.current?.click() : undefined} />
          {!folderId ? (
            <Placeholder>
              <FolderOpenOutlinedIcon style={{ fontSize: 44, color: '#dcdfe6' }} />
              <h4>Select a folder</h4>
              <p>Choose a folder on the left to view its documents{canEdit ? ', or create one to start storing portfolio files.' : '.'}</p>
            </Placeholder>
          ) : (
            <>
              <HeadRow>
                <Crumb>
                  <FolderOpenOutlinedIcon style={{ fontSize: 20, color: '#D24724' }} />
                  <FolderTitle>{selectedFolder.name}</FolderTitle>
                  <Count>{total} {total === 1 ? 'document' : 'documents'}</Count>
                </Crumb>
                {canEdit && (
                  <UploadBtn $busy={busy}>
                    {busy ? <CircularProgress size={15} style={{ color: '#fff' }} /> : <UploadFileOutlinedIcon style={{ fontSize: 18 }} />}
                    {busy ? 'Uploading…' : 'Upload'}
                    <input ref={uploadInputRef} type="file" multiple onChange={handleUpload} />
                  </UploadBtn>
                )}
              </HeadRow>

              {(total > 0 || search || fileTypeIds.length > 0) && (
                <FilterBar>
                  <SearchWrap>
                    <SearchIcon style={{ fontSize: 18, color: '#9aa0ac' }} />
                    <SearchInput
                      placeholder="Search documents…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && <ClearBtn onClick={() => setSearch('')} title="Clear search">×</ClearBtn>}
                  </SearchWrap>
                  {fileTypeOptions.length > 0 && (
                    <Autocomplete
                      multiple
                      size="small"
                      options={fileTypeOptions}
                      value={selectedTypeOptions}
                      onChange={(e, val) => setFileTypeIds(val.map((t) => t.id))}
                      getOptionLabel={(o) => o.name || ''}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      limitTags={2}
                      disableCloseOnSelect
                      sx={{ minWidth: 240, maxWidth: 380, flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder={fileTypeIds.length ? '' : 'All document types'} />
                      )}
                    />
                  )}
                  {(search || fileTypeIds.length > 0) && (
                    <ResultCount>{total} {total === 1 ? 'match' : 'matches'}</ResultCount>
                  )}
                </FilterBar>
              )}

              {canEdit && selectedIds.length > 0 && (
                <BulkBar>
                  <BulkCount>{selectedIds.length} selected</BulkCount>
                  <BulkBtn onClick={() => setBulkMoveOpen(true)}>
                    <DriveFileMoveOutlinedIcon style={{ fontSize: 16 }} /> Move
                  </BulkBtn>
                  {buildingPassports.length > 0 && (
                    <BulkBtn onClick={() => setBulkShareOpen(true)}>
                      <IosShareOutlinedIcon style={{ fontSize: 16 }} /> Add to Building Passports
                    </BulkBtn>
                  )}
                  <BulkBtn $danger onClick={() => setBulkRemoveOpen(true)}>
                    <DeleteOutlineIcon style={{ fontSize: 16 }} /> Remove
                  </BulkBtn>
                  <BulkSpacer />
                  {items.length > 0 && (
                    <TextLink onClick={toggleSelectAll}>
                      {allVisibleSelected ? 'Deselect all' : 'Select all'}
                    </TextLink>
                  )}
                  <TextLink onClick={clearSelection}>Clear</TextLink>
                </BulkBar>
              )}

              {items.length === 0 ? (
                <Placeholder>
                  <DescriptionOutlinedIcon style={{ fontSize: 40, color: '#dcdfe6' }} />
                  <h4>{search || fileTypeIds.length > 0 ? 'No matching documents' : 'No documents yet'}</h4>
                  <p>{search || fileTypeIds.length > 0
                    ? 'Try a different search or document type.'
                    : (canEdit ? 'Upload company or portfolio-level documents to this folder.' : 'No documents have been added to this folder.')}</p>
                </Placeholder>
              ) : (
                <FileList ref={fileListRef} onMouseDown={handleMouseDown} onClick={handleContentClick}>
                  {isDragSelecting && selectionRect && <SelectionOverlay style={selectionRect} />}
                  {items.map((f) => {
                    const url = fileUrl(f)
                    const size = formatSize(f.size || f.latestVersion?.size)
                    const thumb = f.thumbnailUrl || f.thumbnail_url
                    const colour = catColour(f)
                    const name = f.displayName || f.name
                    const bpCount = f.buildingShareCount || f.building_share_count || 0
                    const selected = selectedIds.includes(f.id)
                    return (
                      <FileCard
                        key={f.id}
                        className='file-card'
                        data-file-id={f.id}
                        $selected={selected}
                      >
                        {canEdit && (
                          <SelectToggle
                            className="select-toggle"
                            $selected={selected}
                            title={selected ? 'Deselect' : 'Select'}
                            onClick={(e) => { e.stopPropagation(); toggleSelect(f.id) }}
                          >
                            {selected
                              ? <CheckCircleIcon style={{ fontSize: 22, color: '#D24724' }} />
                              : <RadioButtonUncheckedIcon style={{ fontSize: 20, color: '#9aa0ac' }} />}
                          </SelectToggle>
                        )}
                        <Thumb $color={colour} onClick={() => setPreviewFile(f)} title="Preview">
                          {thumb ? <img src={thumb} alt="" draggable={false} /> : <DescriptionOutlinedIcon style={{ fontSize: 36, color: colour }} />}
                          <TypeTag $color={colour}>{fileTypeName(f)}</TypeTag>
                        </Thumb>
                        <CardBody>
                          <CardName onClick={() => setPreviewFile(f)} title={name}>{name}</CardName>
                          {(uploaderName(f) || formatDate(f.createdAt)) && (
                            <CardSub>
                              {uploaderName(f) ? `By ${uploaderName(f)}` : ''}
                              {uploaderName(f) && formatDate(f.createdAt) ? ' · ' : ''}
                              {formatDate(f.createdAt) || ''}
                            </CardSub>
                          )}
                          {f.description && <CardDesc title={f.description}>{f.description}</CardDesc>}
                          <CardMetaRow>
                            {size && <MetaText>{size}</MetaText>}
                            {bpCount > 0 && (
                              <BpChip onClick={() => setBpSharesFile(f)} title="See the Building Passports this is shared with">
                                <ApartmentOutlinedIcon style={{ fontSize: 13 }} />
                                {bpCount} Building Passport{bpCount === 1 ? '' : 's'}
                              </BpChip>
                            )}
                          </CardMetaRow>
                        </CardBody>
                        <CardActions>
                          <IconAct title="Guru insight" onClick={() => setInsightFile(f)}><AutoAwesomeOutlinedIcon style={{ fontSize: 17 }} /></IconAct>
                          {url && <IconAct as="a" href={url} target="_blank" rel="noreferrer" title="Download"><FileDownloadOutlinedIcon style={{ fontSize: 17 }} /></IconAct>}
                          {canEdit && <IconAct title="Edit details" onClick={() => setEditFile(f)}><EditOutlinedIcon style={{ fontSize: 17 }} /></IconAct>}
                          {canEdit && <IconAct title="Move to another folder" onClick={() => setMoveFileItem(f)}><DriveFileMoveOutlinedIcon style={{ fontSize: 17 }} /></IconAct>}
                          {canEdit && <IconAct title="Share" onClick={() => setShareFile(f)}><IosShareOutlinedIcon style={{ fontSize: 17 }} /></IconAct>}
                          {canEdit && <IconAct $danger title="Remove" onClick={() => setRemoveFileItem(f)}><DeleteOutlineIcon style={{ fontSize: 17 }} /></IconAct>}
                        </CardActions>
                      </FileCard>
                    )
                  })}
                </FileList>
              )}
              {items.length > 0 && items.length < total && <LoadMore onClick={loadMore}>Load more</LoadMore>}
            </>
          )}
        </Main>
      </Layout>

      {accessFolder && (
        <ManagePortfolioFolderPermissions
          portfolioId={portfolioId}
          folder={accessFolder}
          onClose={() => setAccessFolder(null)}
        />
      )}

      {previewFile && extOf(previewFile) === 'pdf' && (
        <PdfFullScreenViewer open onClose={() => setPreviewFile(null)} file={previewFile} skipAccessLog />
      )}
      {previewFile && IMAGE_EXT.includes(extOf(previewFile)) && (
        <ImageViewer open onClose={() => setPreviewFile(null)} file={previewFile} />
      )}
      {previewFile && extOf(previewFile) !== 'pdf' && !IMAGE_EXT.includes(extOf(previewFile)) && (
        <PortfolioFilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}

      {insightFile && (
        <GuruInsightModal
          open
          portfolioId={portfolioId}
          file={insightFile}
          onClose={() => setInsightFile(null)}
        />
      )}

      {shareFile && (
        <SharePortfolioFileModal
          portfolioId={portfolioId}
          file={shareFile}
          buildingPassports={buildingPassports}
          onClose={() => setShareFile(null)}
        />
      )}

      {bpSharesFile && (
        <PortfolioFileBuildingSharesModal
          portfolioId={portfolioId}
          file={bpSharesFile}
          canEdit={canEdit}
          onChanged={() => folderId && dispatch(getPortfolioFolderFilesRoutine.trigger({ portfolioId, folderId, page: 1, search, fileTypeIds }))}
          onClose={() => setBpSharesFile(null)}
        />
      )}

      {moveFileItem && (
        <MoveDocumentModal
          portfolioId={portfolioId}
          file={moveFileItem}
          fromFolderId={folderId}
          onClose={() => setMoveFileItem(null)}
        />
      )}

      {editFile && (
        <EditDocumentModal
          portfolioId={portfolioId}
          file={editFile}
          onSaved={refreshFiles}
          onClose={() => setEditFile(null)}
        />
      )}

      {bulkMoveOpen && (
        <BulkMoveDocumentsModal
          portfolioId={portfolioId}
          fileIds={selectedIds}
          fromFolderId={folderId}
          onMoved={clearSelection}
          onClose={() => setBulkMoveOpen(false)}
        />
      )}

      {bulkShareOpen && (
        <BulkShareToBpsModal
          portfolioId={portfolioId}
          fileIds={selectedIds}
          buildingPassports={buildingPassports}
          onShared={() => { clearSelection(); refreshFiles() }}
          onClose={() => setBulkShareOpen(false)}
        />
      )}

      {bulkRemoveOpen && (
        <ConfirmModal
          title="Remove documents"
          message={`Remove ${selectedIds.length} document${selectedIds.length === 1 ? '' : 's'} from this portfolio? They're soft-deleted and can be restored by an administrator if needed.`}
          confirmLabel="Remove"
          onConfirm={() => { bulkRemove(); clearSelection(); setBulkRemoveOpen(false) }}
          onClose={() => setBulkRemoveOpen(false)}
        />
      )}

      {removeFileItem && (
        <ConfirmModal
          title="Remove document"
          message={`Remove "${removeFileItem.displayName || removeFileItem.name}" from this portfolio? It's soft-deleted and can be restored by an administrator if needed.`}
          confirmLabel="Remove"
          onConfirm={() => { remove(removeFileItem.id); setRemoveFileItem(null) }}
          onClose={() => setRemoveFileItem(null)}
        />
      )}

      {uploadProgress && (() => {
        const { phase, total, uploaded = 0, completed = 0, failed = 0, done } = uploadProgress
        const pct = phase === 'uploading'
          ? Math.round((uploaded / Math.max(total, 1)) * 100)
          : Math.round(((completed + failed) / Math.max(total, 1)) * 100)
        return (
          <UploadPanel>
            <UploadPanelHead>
              {phase === 'uploading' ? (
                <><UploadFileOutlinedIcon style={{ fontSize: 18 }} /> Uploading {total} document{total === 1 ? '' : 's'}…</>
              ) : done ? (
                <><CheckCircleOutlineIcon style={{ fontSize: 18 }} /> Upload complete{failed ? `: ${failed} failed` : ''}</>
              ) : (
                <><CircularProgress size={14} sx={{ color: '#fff' }} /> Processing {total} document{total === 1 ? '' : 's'}…</>
              )}
            </UploadPanelHead>
            <Bar><BarFill $pct={pct} /></Bar>
            <UploadListBox>
              {phase === 'uploading' ? (
                <UploadRow><span>Uploaded {uploaded} of {total}</span><StatusPill $c="#b58a00">Uploading</StatusPill></UploadRow>
              ) : (uploadProgress.files || []).length === 0 ? (
                <UploadRow><span>Queued for processing…</span></UploadRow>
              ) : (
                (uploadProgress.files || []).map((f, i) => {
                  const m = statusMeta(f.status)
                  return (
                    <UploadRow key={i}>
                      <span title={f.displayName}>{f.displayName}</span>
                      <StatusPill $c={m.c}>{m.label}</StatusPill>
                    </UploadRow>
                  )
                })
              )}
            </UploadListBox>
          </UploadPanel>
        )
      })()}
    </>
  )
}
